import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

const UserFormDialog = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  mode,
  availableRoles = [],
  showAssociatedAccount = true, // only true in PropertyAccountsTable
  hideRoleSelection = false,
}) => {
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const roles = Array.isArray(formData.Roles) ? formData.Roles : [];

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const validatePasswords = () => {
    if ((formData.Password || "").length < 8) {
      setPasswordError("Password must be at least 8 characters long.");
      return false;
    }
    if (formData.Password !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleSubmit = () => {
    if (mode === "add" && !validatePasswords()) return;

    // Normalize AssociatedAccounts before submit
    const normalizedData = {
      ...formData,
      AssociatedAccounts: Array.isArray(formData.AssociatedAccounts)
        ? formData.AssociatedAccounts.map((acc) => acc.trim()).filter(Boolean)
        : [],
    };

    onSubmit(normalizedData);

    // Show snackbar after successful submit
    setOpenSnackbar(true);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{mode === "add" ? "Add New User" : "Edit User"}</DialogTitle>

        <DialogContent>
          <TextField
            label="Username"
            fullWidth
            margin="normal"
            value={formData.Username || ""}
            onChange={handleChange("Username")}
          />

          {showAssociatedAccount && (
            <Autocomplete
              multiple
              freeSolo
              filterSelectedOptions
              options={[]} // no preset options
              value={formData.AssociatedAccounts || []}
              onChange={(e, newValue) =>
                setFormData({
                  ...formData,
                  AssociatedAccounts: newValue.map((acc) =>
                    acc.trim()
                  ).filter(Boolean),
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Associated Accounts"
                  margin="normal"
                  fullWidth
                  helperText="Type an account ID and press Enter"
                />
              )}
            />
          )}

          {mode === "add" && (
            <>
              <TextField
                label="Password"
                type="password"
                fullWidth
                required
                margin="normal"
                value={formData.Password || ""}
                onChange={handleChange("Password")}
                error={!!passwordError}
              />
              <TextField
                label="Confirm Password"
                type="password"
                fullWidth
                required
                margin="normal"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                error={!!passwordError}
                helperText={passwordError}
              />
            </>
          )}

          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={formData.Name || ""}
            onChange={handleChange("Name")}
          />

          <TextField
            label="Lastname"
            fullWidth
            margin="normal"
            value={formData.Lastname || ""}
            onChange={handleChange("Lastname")}
          />

          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={formData.Email || ""}
            onChange={handleChange("Email")}
          />

          {!hideRoleSelection && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={roles}
                onChange={(e) =>
                  setFormData({ ...formData, Roles: e.target.value })
                }
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) =>
                  availableRoles
                    .filter((role) => selected.includes(role.RoleID))
                    .map((role) => role.RoleName)
                    .join(", ")
                }
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.RoleID} value={role.RoleID}>
                    <Checkbox checked={roles.includes(role.RoleID)} />
                    <ListItemText primary={role.RoleName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.SendInvite || false}
                onChange={(e) =>
                  setFormData({ ...formData, SendInvite: e.target.checked })
                }
              />
            }
            label="Send invitation email"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {mode === "add"
            ? "User added successfully!"
            : "User updated successfully!"}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserFormDialog;
